"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { ReviewCard } from "@/components/reviews/review-card";
import { scrollToAdjacent } from "@/components/optilase/carousel-page";
import type { GoogleReview } from "@/lib/google-reviews";
import { useRovingFocus } from "@/lib/use-roving-focus";
import { useHorizontalOverflow } from "@/lib/use-scrollable";

// Matches ReviewCard's truncation rule (default `tall = false`).
const TRUNCATE_AT = 180;

const COLUMN = "mx-auto w-full max-w-360 px-5 md:px-10 lg:px-20";

export function ReviewsScroller({
  reviews,
  title,
  cardColor,
  link,
}: {
  reviews: GoogleReview[];
  title?: string;
  link?: { label: string; href: string; target?: string };
  cardColor?: string;
}) {
  const focusableIndices = useMemo(
    () => reviews.map((r, i) => (r.text.length > TRUNCATE_AT ? i : -1)).filter((i) => i !== -1),
    [reviews],
  );

  const firstFocusable = focusableIndices[0] ?? -1;
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const canPage = useHorizontalOverflow(scrollerRef);
  const cardRefs = useRef<(HTMLLIElement | null)[]>([]);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Track scroll position so the edge fades only show where there's more.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const update = () => {
      const maxScroll = el.scrollWidth - el.clientWidth;
      setAtStart(el.scrollLeft <= 1);
      setAtEnd(el.scrollLeft >= maxScroll - 1);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [reviews.length]);

  const revealCard = (index: number) => {
    const scroller = scrollerRef.current;
    const card = cardRefs.current[index];
    if (!scroller || !card) return;
    const sRect = scroller.getBoundingClientRect();
    const cRect = card.getBoundingClientRect();
    const cardLeft = cRect.left - sRect.left + scroller.scrollLeft;
    const target = cardLeft + cRect.width / 2 - scroller.clientWidth / 2;
    const max = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    scroller.scrollTo({
      left: Math.max(0, Math.min(target, max)),
      behavior: "smooth",
    });
  };

  const scrollByPage = (dir: number) => {
    const el = scrollerRef.current;
    if (el) scrollToAdjacent(el, dir > 0 ? 1 : -1);
  };

  const handleKey = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (focusableIndices.length === 0) return;
    const pos = focusableIndices.indexOf(index);
    if (pos === -1) return;
    let nextPos = pos;
    if (e.key === "ArrowRight") nextPos = (pos + 1) % focusableIndices.length;
    else if (e.key === "ArrowLeft")
      nextPos = (pos - 1 + focusableIndices.length) % focusableIndices.length;
    else if (e.key === "Home") nextPos = 0;
    else if (e.key === "End") nextPos = focusableIndices.length - 1;
    else return;
    e.preventDefault();
    const nextIndex = focusableIndices[nextPos];

    buttonRefs.current[nextIndex]?.focus({ preventScroll: true });
    revealCard(nextIndex);
  };

  const { ref: arrowsRef, rovingProps: arrowsRovingProps } = useRovingFocus<HTMLDivElement>();

  const arrowClass =
    "flex size-12 items-center justify-center rounded-full border border-teal-04 text-teal-07 transition-colors hover:border-teal-07 hover:bg-teal-01 aria-disabled:pointer-events-none aria-disabled:opacity-40";

  return (
    <>
      <div className={`flex items-center gap-6 ${COLUMN}`}>
        {title && <h2 className="text-section text-teal-08 flex-1">{title}</h2>}
        {reviews.length > 1 && canPage && (
          <div
            ref={arrowsRef}
            {...arrowsRovingProps}
            className="hidden shrink-0 items-center gap-2 lg:flex"
          >
            <button
              type="button"
              data-roving
              onClick={() => scrollByPage(-1)}
              aria-disabled={atStart || undefined}
              aria-label="Previous reviews"
              className={arrowClass}
            >
              <ChevronLeft className="size-5" strokeWidth={2} />
            </button>
            <button
              type="button"
              data-roving
              onClick={() => scrollByPage(1)}
              aria-disabled={atEnd || undefined}
              aria-label="Next reviews"
              className={arrowClass}
            >
              <ChevronRight className="size-5" strokeWidth={2} />
            </button>
          </div>
        )}
      </div>
      <div className={`relative mt-8 lg:mt-10 ${COLUMN} max-lg:px-0`}>
        <div
          ref={scrollerRef}
          role="group"
          aria-label="Patient reviews"
          tabIndex={0}
          className="focus-visible:outline-teal-07 scrollbar-none overflow-x-auto overflow-y-clip py-1 [-ms-overflow-style:none] focus:outline-none focus-visible:rounded-[12px] focus-visible:outline-2 focus-visible:-outline-offset-2 [&::-webkit-scrollbar]:hidden"
        >
          <ul className="flex w-max gap-5 pr-5 pl-5 md:pl-10 lg:px-0">
            {reviews.map((review, i) => (
              <li
                key={i}
                ref={(el) => {
                  cardRefs.current[i] = el;
                }}
                className="w-[285px] shrink-0 lg:w-[305px]"
              >
                <ReviewCard
                  cardColor={cardColor}
                  review={review}
                  index={i}
                  buttonRef={(el) => {
                    buttonRefs.current[i] = el;
                  }}
                  tabIndex={i === firstFocusable ? 0 : -1}
                  onKeyDown={(e) => handleKey(e, i)}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>

      {link ? (
        <div className={`mt-6 lg:mt-8 ${COLUMN}`}>
          <a
            href={link.href}
            {...(link.target ? { target: link.target } : {})}
            className="text-teal-08 inline-flex items-center gap-1.75 py-2.5 text-base leading-[1.054] font-extrabold hover:underline"
          >
            {link.label}
            <ArrowIcon />
          </a>
        </div>
      ) : null}
    </>
  );
}

function ArrowIcon() {
  return (
    <svg width="17" height="11" viewBox="0 0 17 11" fill="none" aria-hidden>
      <path
        d="M1 5.5h14.5M11 1l4.5 4.5L11 10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
